import { useMemo, useState } from "react";
import Reveal from "@/components/feature/Reveal";
import SectionHeading from "@/pages/home/components/SectionHeading";
import { comments, pollOptions } from "@/mocks/gossip";

const toneStyle: Record<string, string> = {
  accent: "bg-accent-100 text-accent-900",
  primary: "bg-primary-100 text-primary-800",
  secondary: "bg-secondary-100 text-secondary-900",
};

export default function DiscussionSection() {
  const [liked, setLiked] = useState<Record<number, boolean>>({});
  const [voted, setVoted] = useState<number | null>(null);

  const pollResult = useMemo(() => {
    const totals = pollOptions.map((opt, idx) => opt.votes + (voted === idx ? 1 : 0));
    const sum = totals.reduce((acc, cur) => acc + cur, 0);
    return { totals, sum };
  }, [voted]);

  return (
    <section id="discuss" className="w-full bg-secondary-900 py-14 md:py-20">
      <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div className="flex items-start gap-4 md:gap-5">
            <span className="mt-1 font-display text-3xl font-black leading-none text-accent-400 md:text-5xl">
              05
            </span>
            <div>
              <h2 className="font-heading text-xl font-bold text-background-50 md:text-3xl">
                网友热议
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-background-200 md:text-[15px]">
                这里才是主战场。争论、考据、玩梗都挤在同一条时间线上，比正文好看一百倍。
              </p>
            </div>
          </div>
          <span className="inline-flex w-fit items-center gap-2 rounded-full border border-background-200/25 px-4 py-2 text-xs font-medium text-background-100">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-400 anim-pulse-dot" />
            1,284 人正在围观
          </span>
        </div>

        <div className="mt-9 grid grid-cols-1 gap-6 md:mt-12 lg:grid-cols-[1.6fr_1fr]">
          <div className="flex flex-col gap-3">
            {comments.map((c, idx) => {
              const isLiked = Boolean(liked[idx]);
              return (
                <Reveal key={c.user} delay={idx * 60}>
                  <article className="rounded-lg border border-background-200/15 bg-background-50 p-4 md:p-5">
                    <div className="flex items-start gap-3.5">
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-heading text-base font-bold ${
                          toneStyle[c.tone] ?? "bg-background-100 text-foreground-700"
                        }`}
                      >
                        {c.initial}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-bold text-foreground-950">{c.user}</span>
                          <span className="text-[11px] text-foreground-500">{c.time}</span>
                        </div>
                        <p className="mt-2 text-xs leading-relaxed text-foreground-700 md:text-sm">
                          {c.content}
                        </p>
                        <div className="mt-3 flex items-center gap-5">
                          <button
                            type="button"
                            onClick={() => setLiked((prev) => ({ ...prev, [idx]: !prev[idx] }))}
                            className={`inline-flex cursor-pointer items-center gap-1.5 text-xs font-medium transition-colors ${
                              isLiked ? "text-primary-600" : "text-foreground-500 hover:text-primary-600"
                            }`}
                          >
                            <i className={isLiked ? "ri-thumb-up-fill" : "ri-thumb-up-line"} />
                            {c.likes + (isLiked ? 1 : 0)}
                          </button>
                          <button
                            type="button"
                            className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-medium text-foreground-500 transition-colors hover:text-secondary-600"
                          >
                            <i className="ri-chat-3-line" />
                            回复 {c.replies}
                          </button>
                          <button
                            type="button"
                            className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-medium text-foreground-500 transition-colors hover:text-secondary-600"
                          >
                            <i className="ri-share-forward-line" />
                            分享
                          </button>
                        </div>
                      </div>
                    </div>
                  </article>
                </Reveal>
              );
            })}

            <div className="flex flex-col gap-3 rounded-lg border border-background-200/15 bg-background-50/10 p-4 sm:flex-row sm:items-center md:p-5">
              <input
                type="text"
                placeholder="说点什么…（演示用，不会真实提交）"
                className="w-full rounded-md border border-background-200/20 bg-background-50/10 px-4 py-3 text-sm text-background-50 outline-none placeholder:text-background-300"
              />
              <button
                type="button"
                className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-semibold text-secondary-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-send-plane-fill" />
                发布评论
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <Reveal>
              <div id="poll" className="rounded-lg bg-background-50 p-5 md:p-6">
                <div className="flex items-center gap-2">
                  <i className="ri-bar-chart-2-line text-lg text-primary-500" />
                  <h3 className="font-heading text-base font-bold text-foreground-950 md:text-lg">
                    你怎么看这次热议？
                  </h3>
                </div>
                <p className="mt-2 text-xs text-foreground-500">点击选项即可投票（演示用）</p>

                <div className="mt-5 flex flex-col gap-2.5">
                  {pollOptions.map((opt, idx) => {
                    const voted_ = voted !== null;
                    const count = pollResult.totals[idx];
                    const percent = pollResult.sum > 0 ? Math.round((count / pollResult.sum) * 100) : 0;
                    return (
                      <button
                        key={opt.label}
                        type="button"
                        onClick={() => setVoted(idx)}
                        className={`relative w-full cursor-pointer overflow-hidden rounded-md border px-4 py-3 text-left transition-colors ${
                          voted === idx
                            ? "border-primary-400 bg-primary-50"
                            : "border-background-200 bg-background-50 hover:border-primary-300"
                        }`}
                      >
                        {voted_ ? (
                          <span
                            className={`absolute inset-y-0 left-0 ${
                              voted === idx ? "bg-primary-100/80" : "bg-background-100"
                            }`}
                            style={{ width: `${percent}%` }}
                          />
                        ) : null}
                        <span className="relative flex items-center justify-between gap-3">
                          <span className="flex items-center gap-2 text-xs font-medium text-foreground-800 md:text-sm">
                            {voted === idx ? (
                              <i className="ri-checkbox-circle-fill text-primary-500" />
                            ) : (
                              <i className="ri-checkbox-blank-circle-line text-foreground-400" />
                            )}
                            {opt.label}
                          </span>
                          {voted_ ? (
                            <span className="shrink-0 text-xs font-bold text-primary-600">
                              {percent}%
                            </span>
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {voted !== null ? (
                  <p className="mt-4 flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-check-line text-accent-600" />
                    已记录你的选择，共 {pollResult.sum.toLocaleString()} 人参与
                  </p>
                ) : null}
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="rounded-lg border border-background-200/15 bg-background-50/10 p-5 md:p-6">
                <h3 className="font-heading text-base font-bold text-background-50">
                  围观小贴士
                </h3>
                <ul className="mt-4 flex flex-col gap-3">
                  {[
                    "先看原帖再看转述，二手信息最容易走样。",
                    "截图可以 P，时间线很难造，交叉比对更靠谱。",
                    "不站队也能聊天，评论区不只有对喷。",
                  ].map((tip) => (
                    <li key={tip} className="flex items-start gap-2.5 text-xs leading-relaxed text-background-200 md:text-sm">
                      <i className="ri-lightbulb-flash-line mt-0.5 text-base text-accent-400" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}